import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, Receipt, LogOut, Tags, Target, MessageSquarePlus, Inbox, Wallet, Users, Bug } from "lucide-react";
import FinlyLogo from "@/components/FinlyLogo";
import AssistantWidget from "@/components/AssistantWidget";
import VoiceFab from "@/components/VoiceFab";
import FeedbackModal from "@/components/FeedbackModal";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const NavLink = ({ to, icon: Icon, label, testId }) => (
    <Link
      to={to}
      data-testid={testId}
      className={`flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
        isActive(to)
          ? "bg-[#0052FF] text-white"
          : "text-[#E2E8F0] hover:bg-[#1B2638]"
      }`}
    >
      <Icon className="w-4 h-4" />
      <span>{label}</span>
    </Link>
  );

  return (
    <div className="min-h-screen bg-[#0B1220] text-[#E2E8F0]" data-testid="app-layout">
      {/* Top Header */}
      <header className="sticky top-0 z-40 border-b border-[#2A3447] bg-[#0B1220]/90 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-3" data-testid="logo-link">
            <FinlyLogo size={54} rounded="18%" />
            <div className="flex flex-col leading-none">
              <span className="font-display text-[27px] font-semibold text-[#F1F5F9]">Finly+</span>
              <span className="text-[15px] uppercase tracking-[0.2em] text-[#94A3B8] mt-1">Tu dinero, en orden</span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1" data-testid="main-nav">
            <NavLink to="/dashboard" icon={LayoutDashboard} label="Resumen" testId="nav-dashboard" />
            <NavLink to="/transactions" icon={Receipt} label="Transacciones" testId="nav-transactions" />
            <NavLink to="/cuentas" icon={Wallet} label="Cuentas" testId="nav-accounts" />
            <NavLink to="/budgets" icon={Target} label="Presupuestos" testId="nav-budgets" />
            <NavLink to="/categories" icon={Tags} label="Categorías" testId="nav-categories" />
            {user?.role === "admin" && (
              <NavLink to="/admin/feedback" icon={Inbox} label="Feedback" testId="nav-admin-feedback" />
            )}
            {user?.role === "admin" && (
              <NavLink to="/admin/testers" icon={Users} label="Testers" testId="nav-admin-testers" />
            )}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right">
              <p className="text-xs text-[#94A3B8] uppercase tracking-[0.15em]">Sesión</p>
              <p className="text-sm font-medium text-[#F1F5F9]" data-testid="user-name">
                {user?.name || user?.email}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFeedbackOpen(true)}
              className="border-[#0052FF]/40 hover:bg-[#0052FF]/15 text-[#7AA2FF] gap-1.5 hidden sm:inline-flex bg-transparent"
              data-testid="open-feedback-btn"
              title="Enviar feedback beta"
            >
              <MessageSquarePlus className="w-4 h-4" />
              <span className="hidden lg:inline">Feedback beta</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFeedbackOpen(true)}
              className="border-[#0052FF]/40 hover:bg-[#0052FF]/15 text-[#7AA2FF] sm:hidden bg-transparent"
              data-testid="open-feedback-btn-mobile"
              aria-label="Enviar feedback beta"
            >
              <MessageSquarePlus className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="border-[#2A3447] hover:bg-[#1B2638] text-[#E2E8F0] bg-transparent"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Mobile nav */}
        <nav className="md:hidden border-t border-[#2A3447] bg-[#0B1220] px-4 py-2 flex items-center gap-1 overflow-x-auto">
          <NavLink to="/dashboard" icon={LayoutDashboard} label="Resumen" testId="mnav-dashboard" />
          <NavLink to="/transactions" icon={Receipt} label="Transacciones" testId="mnav-transactions" />
          <NavLink to="/cuentas" icon={Wallet} label="Cuentas" testId="mnav-accounts" />
          <NavLink to="/budgets" icon={Target} label="Presupuestos" testId="mnav-budgets" />
          <NavLink to="/categories" icon={Tags} label="Categorías" testId="mnav-categories" />
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-8">{children}</main>
      <AssistantWidget />
      <VoiceFab />

      {/* Floating "Reportar bug / sugerencia" FAB — sits above VoiceFab. */}
      <button
        type="button"
        onClick={() => setFeedbackOpen(true)}
        aria-label="Reportar bug o sugerencia"
        title="Reportar bug / sugerencia"
        data-testid="feedback-fab"
        className="fixed z-50 bottom-[120px] right-[18px] md:bottom-[112px] md:right-[34px] w-12 h-12 rounded-full bg-[#131C2E] hover:bg-[#1B2638] active:scale-95 text-[#7AA2FF] border border-[#0052FF]/40 shadow-[0_8px_22px_-6px_rgba(0,82,255,0.45)] flex items-center justify-center transition-all"
      >
        <Bug className="w-5 h-5" />
        <span className="sr-only">Reportar bug</span>
      </button>

      <FeedbackModal open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </div>
  );
}
